import React from 'react';
import PropTypes from 'prop-types';

import Button from 'components/button';
import KebabButton from 'components/button/kebab';

import StorefrontListItemCard from './list-item-card';
import StorefrontPreviewImage from './preview-image';
import StorefrontUseCases from './use-cases';
import StorefrontIcon from './icon';

import { formatDate } from 'utils';
import { isOwnApp, renderAppPrice } from 'utils/app';
import { renderWithStorefrontLimitTooltip } from 'utils/storefront';

import './storefront.scss';

export default class StorefrontApp extends React.PureComponent {
  static contextTypes = {
    client: PropTypes.object.isRequired,
  };

  static propTypes = {
    app: PropTypes.object.isRequired,
    apps: PropTypes.object.isRequired,
    createStorefront: PropTypes.func.isRequired,
    fetchAppList: PropTypes.func,
    fetchInstalledApps: PropTypes.func,
    loadAllStorefronts: PropTypes.func,
    onViewApp: PropTypes.func,
    inLibrary: PropTypes.bool,
  };

  state = {
    styleId: null,
    styleAssets: null,
    showThemeInfo: false,
    showAppInfo: false,
  };

  onClickInstall = (event) => {
    event.preventDefault();
    const { app, createStorefront } = this.props;

    createStorefront(app, {
      $style: this.state.styleId,
    });
  };

  onChangeThemeStyle = (styleId, styleAssets) => {
    this.setState({ styleId, styleAssets });
  };

  onClickThemeInfo = (event) => {
    event.preventDefault();
    this.setState({ showThemeInfo: true });
  };

  onCloseThemeInfo = () => {
    this.setState({ showThemeInfo: false, showAppInfo: false });
  };

  onClickAppInfo = (event) => {
    event.preventDefault();
    this.setState({ showAppInfo: true });
  };

  onCloseAppInfo = () => {
    this.setState({ showAppInfo: false });
  };

  onClickApp = (event) => {
    event.preventDefault();
    const { app, onViewApp } = this.props;
    onViewApp(app);
  };

  onClickUninstallDelete = (event) => {
    event.preventDefault();
    const { app, onUninstallDeleteApp } = this.props;
    onUninstallDeleteApp(app);
  };

  renderPreviewImage(app) {
    const { inLibrary } = this.props;
    const { styleAssets } = this.state;

    return (
      <StorefrontPreviewImage
        app={app}
        height={360}
        styleAssets={styleAssets}
        onClick={inLibrary ? undefined : this.onClickApp}
        onCompletedAsync={
          inLibrary ? this.onCompletedAsyncLoading.bind(this) : undefined
        }
      />
    );
  }

  async onCompletedAsyncLoading() {
    const { fetchAppList, fetchInstalledApps, loadAllStorefronts } = this.props;

    await Promise.all([
      fetchAppList(),
      fetchInstalledApps(),
      loadAllStorefronts(),
    ]);
  }

  renderAction() {
    const { app, inLibrary, storefronts } = this.props;
    const { type, upload_theme, date_created, async_error, async_progressing } =
      app;

    if (!inLibrary) {
      return null;
    }

    const itemLabel = type === 'theme' ? 'theme' : 'app';

    return (
      <div className="storefront-apps-footer">
        <div className="storefront-apps-actions">
          {renderWithStorefrontLimitTooltip(storefronts, (limitReached) => (
            <Button
              type="secondary"
              size="md"
              disabled={Boolean(
                limitReached || async_progressing || async_error,
              )}
              onClick={this.onClickInstall}
            >
              Create storefront
            </Button>
          ))}
          <KebabButton
            side="bottom"
            buttonSize="md"
            disabled={async_progressing}
            items={[
              <button
                key="delete"
                className="danger"
                onClick={this.onClickUninstallDelete}
              >
                Delete {itemLabel}
              </button>,
              ...(upload_theme
                ? [
                    <hr key="hr-uploaded" />,
                    <label>Uploaded {formatDate(date_created, 'long')}</label>,
                  ]
                : []),
            ]}
          />
        </div>
      </div>
    );
  }

  renderTitleExtra() {
    const { app } = this.props;
    const { client } = this.context;
    const { version, async_progressing } = app;

    if (isOwnApp(app, client)) {
      // Don't show version while uploading
      if (!async_progressing) {
        return version;
      }
    } else {
      return renderAppPrice(app, client);
    }
  }

  render() {
    const { app } = this.props;
    const { showThemeInfo, showAppInfo } = this.state;
    const { name, theme } = app;

    return (
      <>
        <StorefrontListItemCard
          image={this.renderPreviewImage(app)}
          title={
            <>
              <StorefrontIcon app={app} />
              {name}
            </>
          }
          titleExtra={this.renderTitleExtra()}
          useCases={<StorefrontUseCases app={app} />}
          theme={theme}
          action={this.renderAction()}
          onChangeThemeStyle={this.onChangeThemeStyle}
        />
        {showThemeInfo && this.renderThemeInfoModal()}
        {showAppInfo && this.renderAppInfoModal()}
      </>
    );
  }
}
